import { useMemo } from 'react';
import { ChartBar, Cable, AlertTriangle, BarChart3, Activity } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { formatDataUsage } from '@/shared/utils/format-utils';
import { ServiceLine, OverageStatus, ServiceLineStatus } from '@/shared/types/serviceLineTypes';
import { buildDataUsage, getBreakdownOfServiceLineStatuses, OrganizationOverviewData, ServiceLinesSummary, UsageSummary } from '@/shared/types/overviewTypes';
import { isSameMonth, isSameYear } from 'date-fns';
import { parseDateFromString } from '@/shared/utils/date-utils';
import {
    getPreviousMonthDataUsage,
    getOverageInfo,
    getExhaustionInfoForServiceLine,
} from '@/shared/utils/serviceLineCalculations';
import {
    getServiceLineStatusBGColorClass,
    SERVICE_LINE_STATUS_VALUE,
} from '@/shared/utils/color-utils-service-lines';
import { getBgColorClass, getBorderColorClass } from '@/shared/utils/color-utils';

interface DashboardSummaryComponentProps {
    organizationOverviewData: OrganizationOverviewData;
    serviceLines: ServiceLine[];
    mode: 'subscription' | 'datapool';
}

export function DashboardSummaryComponent({
    organizationOverviewData,
    serviceLines,
    mode,
}: DashboardSummaryComponentProps) {
    // Build data usages from summary data
    const overviewDataUsages = useMemo(() => {
        return organizationOverviewData?.summary?.map(month => buildDataUsage(month)) ?? [];
    }, [organizationOverviewData]);

    // Get current month summary data
    const currentMonthAccountSummaryData = useMemo(() => {
        return organizationOverviewData?.summary?.find(summary => isSameMonth(parseDateFromString(summary.startDate), new Date()) && isSameYear(parseDateFromString(summary.startDate), new Date())) ?? null;
    }, [organizationOverviewData]);

    // Get current month usage summary
    const currentMonthUsageSummary = currentMonthAccountSummaryData?.usageSummary;

    // Get current month service lines summary
    const currentMonthServiceLinesSummary = currentMonthAccountSummaryData?.serviceLinesSummary;

    const cards = [
        currentMonthUsageSummary
            ? { key: 'consumption', node: DataConsumptionCard(mode, currentMonthUsageSummary) }
            : null,
        currentMonthUsageSummary
            ? { key: 'forecast', node: DataForecastCard(mode, currentMonthUsageSummary) }
            : null,
        currentMonthServiceLinesSummary
            ? { key: 'status', node: ServiceLineStatusCard(currentMonthServiceLinesSummary) }
            : null,
        mode === 'subscription' && serviceLines.length > 0
            ? { key: 'overage', node: OverageCard(serviceLines) }
            : null,
        serviceLines.length > 0
            ? { key: 'risk', node: ExhaustionRiskCard(serviceLines) }
            : null,
    ].filter((card): card is { key: string; node: JSX.Element } => card != null);

    // Full class names required — Tailwind cannot detect dynamic `grid-cols-${n}` strings.
    const gridColsClass =
        cards.length === 1 ? 'grid-cols-1'
            : cards.length === 2 ? 'grid-cols-1 md:grid-cols-2'
                : cards.length === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                    : cards.length === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
                        : cards.length === 5 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5'
                            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

    if (cards.length === 0) return null;

    return (
        <div className={`mb-4 grid gap-4 ${gridColsClass}`}>
            {cards.map(({ key, node }) => (
                <div key={key} className="h-full">{node}</div>
            ))}
        </div>
    );

    function DataConsumptionCard(mode: 'subscription' | 'datapool', currentMonthUsageSummary: UsageSummary) {
        // Get previous month total data usage
        const previousMonthUsage = getPreviousMonthDataUsage(overviewDataUsages)?.totalDataUsageGB || 0;

        // Get deviation from last month
        const deviationFromPreviousMonthPct = (previousMonthUsage === 0 || currentMonthUsageSummary?.cappedForecastGB === 0)
            ? 0
            : ((currentMonthUsageSummary?.cappedForecastGB - previousMonthUsage) / previousMonthUsage) * 100;

        // Get current month data usage
        const totalDataUsage = currentMonthUsageSummary?.totalDataUsageGB ?? 0;

        // Get current month data usage
        const totalDataPlan = useMemo(() => {
            if (mode === 'subscription') {
                return currentMonthUsageSummary.basePlan;
            } else {
                return currentMonthUsageSummary.contractedDatapool;
            }
        }, [currentMonthUsageSummary, mode]);

        // Get current month usage percentage
        const usageBarPct = useMemo(() => {
            return (totalDataPlan === 0 || totalDataUsage === 0)
                ? 0
                : ((totalDataUsage / totalDataPlan) * 100);
        }, [totalDataPlan, totalDataUsage]);

        const usageBarColor = usageBarPct > 95
            ? 'red'
            : usageBarPct > 80
                ? 'amber'
                : 'green';
        return (
            <div className={`p-4 h-full border-l-4 border rounded-lg ${getBorderColorClass(usageBarColor)}`}>
                <div className="pb-2">
                    <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <ChartBar className="h-4 w-4" />
                        Data Consumption
                    </h2>
                </div>
                <div className="space-y-3">
                    {/* L2 — used / contracted */}
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl font-bold tracking-tight">
                            {formatDataUsage(totalDataUsage) + (mode === 'datapool' && totalDataPlan > 0 ? ` / ${formatDataUsage(totalDataPlan)}` : '')}
                        </span>
                    </div>
                    {/* L3 — usage bar */}
                    <div className="space-y-2">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                            <div className={`h-full rounded-full ${getBgColorClass(usageBarColor)}`} style={{ width: `${usageBarPct}%` }} />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                            <span>Usage</span>
                            <Badge variant="outline" className="text-xs">{usageBarPct.toFixed(2)}%</Badge>
                        </div>
                    </div>
                    {/* L4 — deviation vs last month */}
                    {previousMonthUsage > 0 && (
                        <div className={`flex items-center gap-1 text-xs font-medium ${deviationFromPreviousMonthPct > 0 ? 'text-red-500' : 'text-green-500'}`}>
                            <span>{deviationFromPreviousMonthPct > 0 ? '▲' : '▼'}</span>
                            <span>{Math.abs(deviationFromPreviousMonthPct).toFixed(2)}% vs last month ({formatDataUsage(previousMonthUsage)})</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function DataForecastCard(mode: 'subscription' | 'datapool', currentMonthUsageSummary: UsageSummary) {
        // Get current month data usage
        const totalDataPlan = useMemo(() => {
            if (mode === 'subscription') {
                return currentMonthUsageSummary.basePlan;
            } else {
                return currentMonthUsageSummary.contractedDatapool;
            }
        }, [currentMonthUsageSummary, mode]);

        // Get current month capped forecast
        const totalCappedForecast = currentMonthUsageSummary?.cappedForecastGB ?? 0;

        // Get current month uncapped forecast
        const totalUncappedForecast = currentMonthUsageSummary?.uncappedForecastGB ?? 0;

        // Derived values used across the four cards
        const cappedForecastPct = totalCappedForecast > 0
            ? (totalCappedForecast / totalDataPlan) * 100
            : 0;

        const uncappedForecastPct = totalUncappedForecast > 0
            ? (totalUncappedForecast / totalDataPlan) * 100
            : 0;

        // Scale bar so values >100% keep correct proportions (e.g. 190% vs 95% → amber = 2× blue).
        // Floor at 100 so the track still represents the full datapool when both are under quota.
        const forecastBarScale = Math.max(cappedForecastPct, uncappedForecastPct, 100);
        const cappedBarWidthPct = forecastBarScale > 0
            ? (Math.max(cappedForecastPct, 0) / forecastBarScale) * 100
            : 0;
        const uncappedBarWidthPct = forecastBarScale > 0
            ? (Math.max(uncappedForecastPct, 0) / forecastBarScale) * 100
            : 0;

        const cappedBarColor = cappedForecastPct > 125
            ? 'red'
            : cappedForecastPct > 110
                ? 'amber'
                : 'green';

        return (
            <div className={`p-4 h-full border-l-4 border rounded-lg ${getBorderColorClass(cappedBarColor)}`}>
                <div className="pb-2">
                    <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <BarChart3 className="h-4 w-4" />
                        Data Forecast (Capped)
                    </h2>
                </div>
                <div className="space-y-3">
                    {/* L2 — capped total */}
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl font-bold tracking-tight">
                            {formatDataUsage(totalCappedForecast)}
                        </span>
                    </div>
                    {/* L3 — capped + uncapped overlay bar (scaled to max projection) */}
                    <div className="space-y-2">
                        <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
                            {/* Uncapped — full projection length */}
                            <div
                                className="absolute inset-y-0 left-0 bg-amber-500"
                                style={{ width: `${uncappedBarWidthPct}%` }}
                            />
                            {/* Capped — overlays uncapped at true ratio */}
                            <div
                                className="absolute inset-y-0 left-0 bg-blue-500"
                                style={{ width: `${cappedBarWidthPct}%` }}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <div className="flex flex-row justify-between w-full items-center gap-1.5">
                                <div className="flex flex-row items-center gap-1.5">
                                    <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                                    <span className="text-xs text-muted-foreground">Capped</span>
                                </div>
                                <Badge variant="outline" className="text-xs">{formatDataUsage(totalCappedForecast)} - {cappedForecastPct.toFixed(2)}%</Badge>
                            </div>
                            <div className="flex flex-row justify-between w-full items-center gap-1.5">
                                <div className="flex flex-row items-center gap-1.5">
                                    <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                                    <span className="text-xs text-muted-foreground">Uncapped</span>
                                </div>
                                <Badge variant="outline" className="text-xs">{formatDataUsage(totalUncappedForecast)} - {uncappedForecastPct.toFixed(2)}%</Badge>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ServiceLineStatusCard(currentMonthServiceLinesSummary: ServiceLinesSummary) {
        // Get number of total service lines
        const numberOfTotalServiceLines = currentMonthServiceLinesSummary?.statuses?.reduce((total, status) => total + status.value, 0) ?? 0;

        // Get breakdown of service line statuses
        const serviceLineStatusBreakdown = getBreakdownOfServiceLineStatuses(currentMonthServiceLinesSummary);

        // Get number of active service lines
        const numberOfActiveServiceLines = serviceLineStatusBreakdown?.[ServiceLineStatus.ACTIVE] ?? 0;

        // Get number of exhausted service lines
        const numberOfExhaustedServiceLines = serviceLineStatusBreakdown?.[ServiceLineStatus.EXHAUSTED] ?? 0;

        // Get number of paused service lines
        const numberOfPausedServiceLines = serviceLineStatusBreakdown?.[ServiceLineStatus.PAUSED] ?? 0;

        // Get number of offline service lines
        const numberOfOfflineServiceLines = serviceLineStatusBreakdown?.[ServiceLineStatus.OFFLINE] ?? 0;

        // Get percentage of service lines by status

        const slCardIndicatorColor = numberOfOfflineServiceLines > 0 ? 'red' : numberOfExhaustedServiceLines > 0 ? 'red' : numberOfPausedServiceLines > 0 ? 'amber' : 'green';

        return (
            <div className={`p-4 h-full border-l-4 border rounded-lg ${getBorderColorClass(slCardIndicatorColor)}`}>
                <div className="pb-2">
                    <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Cable className="h-4 w-4" />
                        Service Line Status
                    </h2>
                </div>
                <div className="space-y-3">
                    {/* L2 — active / total */}
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl font-bold tracking-tight">{numberOfActiveServiceLines} / {numberOfTotalServiceLines}</span>
                        <span className="text-base font-medium text-muted-foreground">active</span>
                    </div>
                    {/* L3 — segmented status bar */}
                    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
                        {numberOfTotalServiceLines > 0 && serviceLineStatusBreakdown && Object.entries(serviceLineStatusBreakdown).map(([status, count]) => (
                            <div
                                className={getServiceLineStatusBGColorClass(status as ServiceLineStatus)}
                                key={status}
                                style={{ width: `${(count / numberOfTotalServiceLines) * 100}%` }} />
                        ))}
                    </div>
                    {/* L4 — color legend */}
                    <div className="grid grid-cols-3 gap-x-3 gap-y-1">
                        {serviceLineStatusBreakdown && Object.entries(serviceLineStatusBreakdown).map(([status, count]) => (
                            <div key={status} className="flex items-center gap-1.5">
                                <span className={`inline-block h-2 w-2 rounded-full ${getServiceLineStatusBGColorClass(status as ServiceLineStatus)}`} />
                                <span className="text-xs text-muted-foreground">{SERVICE_LINE_STATUS_VALUE[status]}</span>
                                <span className="ml-auto text-xs font-semibold">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    function OverageCard(serviceLines: ServiceLine[]) {
        // Get total overage incurred
        const totalOverageSubscribed = serviceLines.reduce((total, sl) => {
            const info = getOverageInfo(sl.overage);
            total += info.status === OverageStatus.ACTIVE ? info.dataAmount : 0;
            return total;
        }, 0);

        const totalSLsWithOverageSubscribed = serviceLines.reduce((total, sl) => {
            const info = getOverageInfo(sl.overage);
            total += info.status === OverageStatus.ACTIVE ? 1 : 0;
            return total;
        }, 0);

        const overageCardColorIndicator = totalOverageSubscribed > 0 ? 'amber' : 'green';

        return (
            <div className={`p-4 h-full border-l-4 border rounded-lg ${getBorderColorClass(overageCardColorIndicator)}`}>
                <div className="pb-2">
                    <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Activity className="h-4 w-4" />
                        Overage
                    </h2>
                </div>
                <div className="space-y-3">
                    {/* L2 — overage incurred */}
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl font-bold tracking-tight">
                            {formatDataUsage(totalOverageSubscribed)}
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-amber-500 text-white">
                            {totalSLsWithOverageSubscribed} service lines with active overage
                        </Badge>
                    </div>
                </div>
            </div>
        );
    }

    function ExhaustionRiskCard(serviceLines: ServiceLine[]) {
        const linesAtRisk = serviceLines.filter(sl => {
            const info = getExhaustionInfoForServiceLine(sl);
            return info.planAdequacy === 'insufficient';
        }).length;

        const linesAtRiskColor = linesAtRisk > 0 ? 'red' : 'green';

        return (
            <div className={`p-4 h-full border-l-4 border rounded-lg ${getBorderColorClass(linesAtRiskColor)}`}>
                <div className="pb-2">
                    <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <AlertTriangle className="h-4 w-4" />
                        Exhaustion Risk
                    </h2>
                </div>
                <div className="space-y-3">
                    <div className="flex flex-col items-baseline gap-1.5">
                        <span className="text-2xl font-bold tracking-tight">
                            {linesAtRisk} lines at risk
                        </span>
                        <span className="text-base font-medium text-muted-foreground">
                            before cycle end
                        </span>
                    </div>
                    {linesAtRisk > 0 && (
                        <div className="space-y-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/30">
                            <p className="text-xs font-medium text-red-600 dark:text-red-400">
                                Enable overage to prevent throttling.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}